import { useCallback, useEffect, useState } from 'react';
import { Confirm, toast, type Destructive } from '@drbell/ui';
import { api, ApiError, type AccessRole, type GrantSummary, type InviteSent, type PatientSummary, type RingingPhones } from '../api.ts';

/**
 * Who can be a hands-on helper — the reminders ring on their phone too.
 * A caregiver or guardian of a family's record; the same rule as
 * `mayRingHere` in @drbell/core, which the server enforces.
 */
const mayRing = (role: string, ownerType: 'user' | 'org') =>
  ownerType === 'user' && (role === 'caregiver' || role === 'guardian');

/**
 * Who looks after them — RX-CARE-01, parity with the phone.
 *
 * The escalation ladder reaches nobody until somebody accepts an invitation,
 * so this says plainly who is invited, who is watching, and when nobody is.
 * Everyone who can see the record sees the list; only someone who may manage
 * access (the person, their owner, a guardian) can invite or remove.
 *
 * Staff and clinician roles are not offered: those come from an
 * organisation's own membership, not from a family member.
 */
const ROLES: Array<{ id: AccessRole; label: string; detail: string }> = [
  { id: 'caregiver', label: 'Caregiver', detail: 'Told if a dose is missed. Can mark doses as taken.' },
  { id: 'guardian', label: 'Guardian', detail: 'Everything a caregiver can do, plus adding and changing medicines.' },
  { id: 'viewer', label: 'Viewer', detail: 'Can see what is due. Not told about missed doses.' },
  { id: 'self', label: 'Their own phone', detail: 'This number is theirs. Their reminders come to it, and it shows the screen chosen for them.' },
];

const ROLE_NAME: Record<string, string> = {
  caregiver: 'Caregiver', guardian: 'Guardian', viewer: 'Viewer', self: 'Their own phone',
  staff: 'Staff', clinician: 'Clinician',
};

/** The invitation just sent, kept on screen so its link can be passed on. */
interface Sent {
  url: string;
  phone: string;
  role: AccessRole;
  notifiedInApp: boolean;
}

export function People({
  patient, canManage, userId, onChanged, onHowTold,
}: {
  patient: PatientSummary;
  canManage: boolean;
  userId: string;
  /** The record changed hands: the session's view of it is stale. */
  onChanged: () => void;
  /** Open "How I'm told" with this record first. */
  onHowTold?: () => void;
}) {
  const [grants, setGrants] = useState<GrantSummary[] | null>(null);
  const [phone, setPhone] = useState('+91');
  const [role, setRole] = useState<AccessRole>('caregiver');
  /** "Gives the medicines by hand": a hands-on helper, whose phone rings too. */
  const [ringsHere, setRingsHere] = useState(false);
  const canRing = mayRing(role, patient.ownerType);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Destructive | null>(null);
  const [sent, setSent] = useState<Sent | null>(null);
  /** Some of the person's phones were left out of ringing — said, read-only. */
  const [someOnly, setSomeOnly] = useState(false);

  const load = useCallback(async () => {
    try {
      setGrants((await api.get<{ grants: GrantSummary[] }>(`/api/patients/${patient.id}/grants`)).grants);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
    // Best effort: a note, not the page.
    api.get<RingingPhones>(`/api/patients/${patient.id}/phones`)
      .then((r) => setSomeOnly(r.someOnly === true))
      .catch(() => setSomeOnly(false));
  }, [patient.id]);

  useEffect(() => {
    setGrants(null);
    void load();
  }, [load]);

  const phoneOk = /^\+[1-9]\d{7,14}$/.test(phone.trim());

  const invite = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<InviteSent>(`/api/patients/${patient.id}/grants`, {
        phone: phone.trim(),
        role,
        // As on the phone: a missed dose is the alert that matters; the rest
        // are opt-in, so the ladder is not noise the moment someone joins.
        notifyOnMissed: true,
        notifyOnLate: role !== 'viewer',
        notifyOnTaken: false,
        // Only where offered: a box ticked before switching to Viewer is not sent.
        ...(canRing && ringsHere ? { ringsHere: true } : {}),
      });
      setRingsHere(false);
      setSent({ url: r.shareUrl, phone: phone.trim(), role, notifiedInApp: r.notifiedInApp });
      toast.success(
        'Invitation ready',
        r.notifiedInApp
          ? `${phone.trim()} uses DrBell and has been told in the app.`
          : `Send ${phone.trim()} the link below so they can approve it.`,
      );
      setPhone('+91');
      await load();
    } catch (e) {
      // The server says why in a sentence (already linked to their own phone,
      // the care home handles family contact, the plan's limit), and a
      // retry fixes none of them — so show its words, not "try again".
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = (g: GrantSummary) =>
    setPending({
      title: `Remove ${g.granteeName ?? g.invitePhone ?? 'this person'}?`,
      body: 'They lose access immediately and stop receiving alerts.',
      reassurance: `${patient.displayName}’s record and reminders are not affected.`,
      confirmLabel: 'Remove',
      onConfirm: async () => {
        setPending(null);
        try {
          await api.del(`/api/patients/${patient.id}/grants/${g.id}`);
          toast.success('Removed');
          await load();
        } catch (e) {
          toast.error('Could not remove', e instanceof ApiError ? e.message : String(e));
        }
      },
    });

  /*
   * Make somebody a hands-on helper, or stop, like the phone. OFF is at
   * once: their phone is asked to cancel the alarms, or an unanswered
   * question is taken back. ON for somebody already helping is a question
   * (ringing needs both sides, 3 Oct 2026): the row says "Asked — waiting"
   * until they accept. On an invitation not yet answered it changes what
   * they will be asked.
   */
  const setRinging = async (g: GrantSummary, on: boolean) => {
    const asking = on && g.status === 'active';
    setGrants((now) => now?.map((x) => (x.id === g.id
      ? asking ? { ...x, ringsHereRequestExpiresAt: Date.now() } : { ...x, ringsHere: on, ringsHereRequestExpiresAt: null }
      : x)) ?? now);
    try {
      const r = await api.put<{ ringsHere: boolean; requested?: boolean; requestExpiresAt?: number; withdrawn?: boolean }>(
        `/api/patients/${patient.id}/grants/${g.id}/ringing`, { ringsHere: on },
      );
      if (r.requested) {
        setGrants((now) => now?.map((x) => (x.id === g.id ? { ...x, ringsHere: false, ringsHereRequestExpiresAt: r.requestExpiresAt ?? Date.now() } : x)) ?? now);
      }
      toast.success(
        r.requested ? 'Asked. Their phone starts ringing once they accept.'
          : r.withdrawn ? 'Request withdrawn'
            : on ? 'Reminders will ring on their phone' : 'Reminders no longer ring on their phone',
      );
    } catch (e) {
      toast.error('Could not change it', e instanceof ApiError ? e.message : String(e));
      await load();
    }
  };

  if (!grants && !error) return <p className="text-(--muted)">Loading…</p>;

  const shown = (grants ?? []).filter((g) => g.status === 'active' || g.status === 'pending');
  // Told means accepted: a pending invitation tells nobody yet.
  /*
   * The signed-in person helps with this record (rather than owning it, or
   * it being their own): how they hear about it is theirs to change, and
   * this is where they would look for it.
   */
  const helping = (grants ?? []).some(
    (g) => g.granteeType === 'user' && g.granteeId === userId && g.status === 'active' && g.role !== 'self',
  );
  const anyoneTold = shown.some(
    (g) => g.status === 'active' && g.role !== 'viewer' && g.role !== 'self' && g.notifyOnMissed,
  );

  return (
    <section className="space-y-6">
      {patient.ownerType === 'org' && (
        <p className="rounded-xl border border-(--line) bg-(--panel) p-4 text-sm text-(--muted)">
          {patient.displayName} is looked after by a care home. Its staff are told about missed doses
          through the home&rsquo;s own team; anyone listed here is told as well.
        </p>
      )}

      {patient.ownerIsTold && (
        <p className="rounded-xl border border-(--line) bg-(--panel) p-4 text-sm text-(--muted)">
          Whoever set up {patient.displayName}&rsquo;s record is told about missed doses as well.
        </p>
      )}

      {/*
        "Ring on: only this phone" is chosen on the phone, by the person whose
        phones they are (the record's settings). Said here so nobody wonders
        why the tablet on the shelf stays quiet; not changeable from here.
      */}
      {someOnly && (
        <p className="rounded-xl border border-(--line) bg-(--panel) p-4 text-sm text-(--muted)">
          Reminders for {patient.displayName} ring only on the phone chosen for them. Their other phones stay
          signed in to see the doses, but do not ring. This is changed in the DrBell app, under the
          record&rsquo;s settings.
        </p>
      )}

      {grants && !anyoneTold && patient.ownerType !== 'org' && !patient.ownerIsTold && (
        <div className="rounded-xl border-2 border-amber-500 p-4">
          <p className="font-medium text-amber-600">Nobody is told if a dose is missed</p>
          <p className="text-sm text-(--muted)">
            {shown.some((g) => g.status === 'pending')
              ? 'Nobody is told until an invitation is accepted.'
              : 'Add someone who should know if a dose is missed.'}
          </p>
        </div>
      )}

      {shown.length > 0 && (
        <ul className="divide-y divide-(--line) rounded-xl border border-(--line)">
          {shown.map((g) => (
            <li key={g.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {/* The name the API sends, before a number — B2C audit. */}
                  {g.granteeName ?? g.invitePhone ?? (g.granteeType === 'org' ? 'A care organisation' : 'Someone')}
                </p>
                <p className="text-sm text-(--muted)">
                  {ROLE_NAME[g.role] ?? g.role} · {g.status === 'pending' ? 'invited, not yet accepted' : 'watching'}
                  {g.role !== 'viewer' && g.role !== 'self' && !g.notifyOnMissed && ' · not told about missed doses'}
                  {g.ringsHere && !canManage && ' · reminders ring on their phone'}
                </p>
                {canManage && mayRing(g.role, patient.ownerType) && (() => {
                  // Asked and not answered: ticked, with the wait said in
                  // words; unticking takes the question back.
                  const asked = !g.ringsHere && Boolean(g.ringsHereRequestExpiresAt);
                  return (
                    <label className="mt-1 flex min-h-11 cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={g.ringsHere === true || asked}
                        onChange={(e) => void setRinging(g, e.target.checked)}
                        aria-describedby={asked ? `asked-${g.id}` : undefined}
                        className="h-4 w-4"
                      />
                      <span>
                        Reminders ring on their phone (they give the medicines)
                        {asked && (
                          <span id={`asked-${g.id}`} className="block text-xs text-(--muted)">
                            {g.granteeName ? `Asked — waiting for ${g.granteeName}` : 'Asked — waiting for an answer'}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })()}
              </div>
              {canManage && (
                <button onClick={() => remove(g)} className="min-h-11 rounded-lg border border-red-500/50 px-3 text-sm text-red-500 hover:bg-red-500/10">
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {helping && onHowTold && (
        <button
          onClick={onHowTold}
          className="min-h-11 text-left text-sm text-(--accent) underline underline-offset-2 hover:no-underline"
        >
          How I&rsquo;m told about {patient.displayName}
        </button>
      )}

      {canManage ? (
        <div className="space-y-3 rounded-xl border border-(--line) p-4">
          <h2 className="text-sm font-medium">Add someone who can help</h2>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputMode="tel"
            placeholder="+919000000000"
            aria-label="Their phone number, with country code"
            className="w-full rounded-lg border border-(--line) bg-(--bg) min-h-11 px-3 font-mono text-sm"
          />
          <fieldset className="space-y-2">
            <legend className="sr-only">What they can do</legend>
            {ROLES.map((r) => (
              <label
                key={r.id}
                className={`flex min-h-11 cursor-pointer gap-3 rounded-lg border p-3 ${role === r.id ? 'border-(--accent) bg-(--accent-soft)' : 'border-(--line)'}`}
              >
                <input type="radio" name="role" checked={role === r.id} onChange={() => setRole(r.id)} className="mt-1" />
                <span>
                  <span className="block text-sm font-medium">{r.label}</span>
                  <span className="block text-sm text-(--muted)">{r.detail}</span>
                </span>
              </label>
            ))}
          </fieldset>
          {canRing && (
            <label className="flex min-h-11 cursor-pointer gap-3 rounded-lg border border-(--line) p-3">
              <input type="checkbox" checked={ringsHere} onChange={(e) => setRingsHere(e.target.checked)} className="mt-1" />
              <span>
                <span className="block text-sm font-medium">Gives the medicines by hand</span>
                <span className="block text-sm text-(--muted)">
                  Reminders will ring on their phone too, and they can mark doses as taken. The family is still
                  told about a missed dose.
                </span>
              </span>
            </label>
          )}
          <button
            disabled={!phoneOk || busy}
            onClick={() => void invite()}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      ) : (
        <p className="text-sm text-(--muted)">
          Only {patient.displayName}, or whoever set up their record, can invite or remove people.
        </p>
      )}

      {sent && <SendLink sent={sent} patient={patient} onClose={() => setSent(null)} />}

      {patient.isSelf && patient.ownerType === 'user' && patient.ownerId && patient.ownerId !== userId && (
        <TakeBack patient={patient} onConfirm={setPending} onChanged={onChanged} />
      )}

      {error && <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">{error}</p>}
      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </section>
  );
}

/**
 * Send them the link — the inviter's own WhatsApp or SMS, not DrBell's.
 *
 * DrBell may only message a number that has agreed to hear from it, and
 * somebody being invited has agreed to nothing yet. So the invitation
 * reaches a person who does not use DrBell only if the inviter passes it on
 * themselves: from their own phone, to someone who knows their number.
 */
function SendLink({ sent, patient, onClose }: { sent: Sent; patient: PatientSummary; onClose: () => void }) {
  const message = sent.role === 'self'
    ? `I have set up DrBell for your medicines. Open this to approve: ${sent.url}`
    : `I have added you on DrBell to help with ${patient.displayName}'s medicines. Open this to approve: ${sent.url}`;
  const text = encodeURIComponent(message);

  const copy = async () => {
    // Shown as well as copied: a refused clipboard write is silent, and a
    // link that did not copy gets an old one pasted in its place.
    try {
      await navigator.clipboard.writeText(sent.url);
      toast.success('Link copied');
    } catch {
      toast.info('Copy the link from here', sent.url);
    }
  };

  const button = 'inline-flex min-h-11 items-center justify-center rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)';

  return (
    <div className="space-y-3 rounded-xl border border-(--accent) bg-(--accent-soft) p-4" aria-live="polite">
      <div className="flex items-start gap-3">
        <h2 className="font-medium">Send them the link</h2>
        <button onClick={onClose} className="ml-auto min-h-11 rounded-lg px-2 text-sm text-(--muted) hover:text-(--fg)">
          Done
        </button>
      </div>
      <p className="text-sm">
        If they already use DrBell they get a notification. Otherwise send this link from your own WhatsApp
        or SMS: DrBell cannot message someone who has not agreed to it.
      </p>
      {sent.notifiedInApp && (
        <p className="text-sm text-(--muted)">{sent.phone} uses DrBell and has been told in the app.</p>
      )}
      <input
        readOnly
        value={sent.url}
        aria-label="Invitation link"
        onFocus={(e) => e.currentTarget.select()}
        className="min-h-11 w-full min-w-0 rounded-lg border border-(--line) bg-(--bg) px-3 font-mono text-xs"
      />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => void copy()} className={`${button} bg-(--bg)`}>Copy link</button>
        <a href={`https://wa.me/${sent.phone.replace(/\D/g, '')}?text=${text}`} target="_blank" rel="noopener noreferrer" className={`${button} bg-(--bg)`}>
          WhatsApp
        </a>
        <a href={`sms:${sent.phone}?&body=${text}`} className={`${button} bg-(--bg)`}>SMS</a>
      </div>
    </div>
  );
}

/**
 * Look after my own medicines — the person takes their record back.
 *
 * Only for the person the record is about, while somebody else owns it (a
 * daughter set it up, and her mother now manages on her own). The owner's
 * name is not something the client is sent — `/me` gives the owner's id and
 * nothing more — so the sentence names them by what they did instead.
 */
function TakeBack({
  patient, onConfirm, onChanged,
}: {
  patient: PatientSummary;
  onConfirm: (d: Destructive) => void;
  onChanged: () => void;
}) {
  const owner = patient.ownerName ?? 'the person who set it up';
  return (
    <div className="space-y-2 rounded-xl border border-(--line) p-4">
      <h2 className="text-sm font-medium">Look after my own medicines</h2>
      <p className="text-sm text-(--muted)">
        Take this record over from {owner}. They will no longer see your medicines or be told about your
        doses, and your own plan will apply.
      </p>
      <button
        onClick={() =>
          onConfirm({
            title: 'Take your record over?',
            body: `${owner[0]!.toUpperCase()}${owner.slice(1)} will no longer see your medicines or be told about your doses, and your own plan will apply.`,
            reassurance: 'Your medicines, reminders and history stay as they are. You can invite them again at any time.',
            confirmLabel: 'Take it over',
            onConfirm: async () => {
              // Thrown on failure, so the dialog shows why and stays open.
              await api.post(`/api/patients/${patient.id}/owner/release`);
              toast.success('This record is yours now');
              onChanged();
            },
          })
        }
        className="min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)"
      >
        Take my record over
      </button>
    </div>
  );
}
